/**
 * The words an invited person sees on the way in: on sign-in, when the link they followed is an
 * invitation, and on the accept page.
 *
 * Sign-in cannot name the team: better-auth's invitation lookup needs a session belonging to
 * the invited address, so before sign-in the line says "a team on AI Space".
 */

const ACCEPT = /^\/accept-invitation\/([^/?#]+)$/;

/** The invitation id in a `?redirect=` to the accept page; null for any other destination. */
export function invitationIdFrom(redirect: unknown): string | null {
  if (typeof redirect !== "string") return null;
  return ACCEPT.exec(redirect)?.[1] ?? null;
}

const clean = (name: string | null | undefined) => (name && name.trim() ? name.trim() : null);

/** The line above the sign-in form for someone following an invitation. */
export function invitedLine(teamName: string | null | undefined): string {
  const name = clean(teamName);
  return `You've been invited to ${name ?? "a team on AI Space"}. Sign in with the email the invitation was sent to.`;
}

/** The accept page's line and button, named after the team once its name has loaded. */
export function joinCopy(teamName: string | null | undefined): { subtitle: string; button: string } {
  const name = clean(teamName);
  return name
    ? { subtitle: `Join ${name} on AI Space.`, button: `Join ${name}` }
    : { subtitle: "Join this team on AI Space.", button: "Accept invitation" };
}

const REFUSALS: Record<string, string> = {
  expired: "This invitation has expired. Ask your instructor to send it again.",
  accepted: "This invitation has already been accepted.",
  canceled: "This invitation was canceled. Ask your instructor for a new one.",
};

/**
 * What the accept page says when accepting fails. better-auth reports an expired, accepted
 * or canceled invitation alike as "Invitation not found"; `state` (from
 * GET /api/org/invitations/:id/state) tells them apart. Any other state keeps
 * better-auth's message.
 */
export function refusalCopy(state: string, message: string | undefined): string {
  return REFUSALS[state] ?? message ?? "Could not accept this invitation.";
}
